import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  ChartLineUp, Users, Briefcase, Clock, ArrowsClockwise, Target,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const fmtHr = (h) => `${(h || 0).toFixed(1)}h`;

export default function Billability() {
  const [month, setMonth] = useState(thisMonth());
  const [summary, setSummary] = useState(null);
  const [byEmp, setByEmp] = useState([]);
  const [byPrj, setByPrj] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: s }, { data: e }, { data: p }] = await Promise.all([
        api.get("/pmo/billability/summary", { params: { month } }),
        api.get("/pmo/billability/by-employee", { params: { month } }),
        api.get("/pmo/billability/by-project", { params: { month } }),
      ]);
      setSummary(s); setByEmp(e); setByPrj(p);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const gaugeTone = useMemo(() => {
    const u = summary?.utilization_percent ?? 0;
    const target = summary?.target_percent ?? 80;
    if (u >= target) return { bg: "bg-zenith-green", text: "text-zenith-green", label: "on target" };
    if (u >= target - 10) return { bg: "bg-amber-500", text: "text-amber-600", label: "close to target" };
    return { bg: "bg-zenith-magenta", text: "text-zenith-magenta", label: "below target" };
  }, [summary]);

  return (
    <div className="space-y-6" data-testid="billability-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zenith-magenta font-bold">Billability & utilization</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Are we paying for time we can't bill?</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Track billable vs non-billable hours per employee, project and month. Target utilization is 80% — anything less is idle capacity, anything more is burn-out risk.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-sm w-40" data-testid="billability-month" />
          <Button variant="outline" onClick={load} className="rounded-sm" data-testid="billability-refresh">
            <ArrowsClockwise size={14} className={busy ? "animate-spin mr-1" : "mr-1"} /> Refresh
          </Button>
        </div>
      </div>

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <UtilizationGauge summary={summary} gaugeTone={gaugeTone} />
          <KPI icon={Clock} label="Billable hours" value={fmtHr(summary.billable_hours)} tone="teal" sub={`out of ${summary.capacity_hours} capacity`} />
          <KPI icon={Users} label="Non-billable" value={fmtHr(summary.non_billable_hours)} tone="magenta" sub={`+ ${fmtHr(summary.leave_hours)} leave`} />
          <KPI icon={Target} label="Active employees" value={summary.active_employees} tone="navy" sub={`${summary.working_days} working days`} />
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-zinc-200 rounded-md bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-sm text-adb-navy">Utilization by employee</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Top 10</div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byEmp.slice(0, 10)} margin={{ top: 5, right: 5, left: -10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
              <XAxis dataKey="name" fontSize={10} angle={-30} textAnchor="end" interval={0} height={40} />
              <YAxis fontSize={10} unit="%" />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2 }} />
              <Bar dataKey="utilization" fill="#12b3a8" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-zinc-200 rounded-md bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-sm text-adb-navy">Billable hours by project</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Top 10</div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byPrj.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 20, left: 30, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
              <XAxis type="number" fontSize={10} />
              <YAxis type="category" dataKey="name" fontSize={10} width={90} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2 }} />
              <Bar dataKey="billable" stackId="a" fill="#33cc33" radius={[0, 3, 3, 0]} />
              <Bar dataKey="non_billable" stackId="a" fill="#e6007a" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Employee table */}
      <div className="border border-zinc-200 rounded-md bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200">
          <div className="font-display font-bold text-sm text-adb-navy">Employee utilization scorecard</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="billability-table">
            <thead className="bg-zinc-50 text-left">
              <tr className="text-[11px] uppercase tracking-widest text-zinc-500">
                <th className="py-2 px-3">Employee</th>
                <th className="py-2 px-3 text-right">Billable</th>
                <th className="py-2 px-3 text-right">Non-bill</th>
                <th className="py-2 px-3 text-right">Leave</th>
                <th className="py-2 px-3 text-right">Logged</th>
                <th className="py-2 px-3 text-right">Capacity</th>
                <th className="py-2 px-3">Utilization</th>
              </tr>
            </thead>
            <tbody>
              {byEmp.map((r) => (
                <tr key={r.employee_id} className="border-t border-zinc-100" data-testid={`bill-row-${r.employee_id}`}>
                  <td className="py-2 px-3">
                    <div className="font-semibold text-adb-navy">{r.name}</div>
                    <div className="text-[10px] text-zinc-500">{r.designation || "—"} · <span className="font-mono-plex">{r.employee_code}</span></div>
                  </td>
                  <td className="py-2 px-3 text-right font-mono-plex font-bold text-zenith-green">{fmtHr(r.billable)}</td>
                  <td className="py-2 px-3 text-right font-mono-plex text-zenith-magenta">{fmtHr(r.non_billable)}</td>
                  <td className="py-2 px-3 text-right font-mono-plex text-zinc-400">{fmtHr(r.leave)}</td>
                  <td className="py-2 px-3 text-right font-mono-plex text-zinc-600">{fmtHr(r.logged)}</td>
                  <td className="py-2 px-3 text-right font-mono-plex text-zinc-400">{r.capacity}h</td>
                  <td className="py-2 px-3 w-56">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div className={`h-full ${r.utilization >= 80 ? "bg-zenith-green" : r.utilization >= 60 ? "bg-amber-500" : "bg-zenith-magenta"}`} style={{ width: `${Math.min(r.utilization, 120)}%` }} />
                      </div>
                      <div className="w-12 text-right text-xs font-mono-plex font-bold">{r.utilization}%</div>
                    </div>
                  </td>
                </tr>
              ))}
              {byEmp.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-zinc-400 text-sm">No timesheet data for {month} — ask team members to log hours.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UtilizationGauge({ summary, gaugeTone }) {
  const u = summary.utilization_percent;
  const angle = (u / 100) * 180;
  return (
    <div className="border border-zinc-200 rounded-md bg-white p-4" data-testid="billability-gauge">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Overall utilization</div>
      <div className="relative h-24 mt-2">
        <svg viewBox="0 0 100 55" className="w-full h-full">
          <path d="M 8 50 A 42 42 0 0 1 92 50" fill="none" stroke="#f4f4f5" strokeWidth="8" strokeLinecap="round" />
          <path
            d="M 8 50 A 42 42 0 0 1 92 50"
            fill="none" strokeWidth="8" strokeLinecap="round"
            className={gaugeTone.bg.replace("bg-", "text-")}
            stroke="currentColor"
            strokeDasharray="132"
            strokeDashoffset={132 - (132 * angle) / 180}
            style={{ transition: "stroke-dashoffset 0.8s ease-out" }}
          />
          <text x="50" y="46" textAnchor="middle" fontSize="16" fontWeight="700" className={gaugeTone.text}>{u}%</text>
        </svg>
      </div>
      <div className="text-center">
        <div className={`text-xs font-semibold ${gaugeTone.text} capitalize`}>{gaugeTone.label}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">Target {summary.target_percent}%</div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, tone, sub }) {
  const toneMap = {
    teal: "text-zenith-teal", green: "text-zenith-green",
    magenta: "text-zenith-magenta", navy: "text-adb-navy",
  };
  return (
    <div className="border border-zinc-200 rounded-md bg-white p-4">
      <div className="flex items-center gap-1.5">
        <Icon size={14} weight="duotone" className={toneMap[tone]} />
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
      </div>
      <div className={`font-display text-2xl font-bold mt-1 ${toneMap[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}
